import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllSections,
  getSectionById,
  updateSection,
  createSection,
  deleteSection,
} from "../../../../../services/sectionsServices";

import {
  getAllCategoriesAsso,
  createCategoryAsso,
  deleteCategoryAsso,
} from "../../../../../services/categoriesAssoServices";

import {
  extractArray,
  getStats,
  isDemoMode,
  normalizeText,
} from "../utils/sectionAdminUtils";

const CHUNK = 50;

const sortCategories = (items = []) => {
  return [...items]
    .filter((category) => category.is_active !== false)
    .sort((a, b) => {
      const ordreA = Number(a.ordre ?? 999);
      const ordreB = Number(b.ordre ?? 999);

      if (ordreA !== ordreB) return ordreA - ordreB;

      return String(a.label || "").localeCompare(String(b.label || ""), "fr", {
        sensitivity: "base",
      });
    });
};

export default function useSectionAdmin() {
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);

  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [visibleCount, setVisibleCount] = useState(CHUNK);

  const [drawer, setDrawer] = useState({
    type: null,
    section: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchSections = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllSections();

      const list = extractArray(data).sort((a, b) => {
        const ordreA = Number(a.ordre ?? 999);
        const ordreB = Number(b.ordre ?? 999);

        if (ordreA !== ordreB) return ordreA - ordreB;

        return String(a.nom_section || "").localeCompare(
          String(b.nom_section || ""),
          "fr",
          { sensitivity: "base" },
        );
      });

      setSections(list);
    } catch (error) {
      console.error("Erreur lors de la récupération des sections :", error);
      toast.error("Erreur lors de la récupération des associations.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await getAllCategoriesAsso();
      const list = sortCategories(extractArray(data));
      setCategories(list);
    } catch (error) {
      console.error("Erreur lors de la récupération des catégories :", error);
      toast.error("Erreur lors de la récupération des catégories.");
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchSections(), fetchCategories()]);
  }, [fetchSections, fetchCategories]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const categoriesById = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc[String(category.id)] = category;
      return acc;
    }, {});
  }, [categories]);

  const stats = useMemo(() => getStats(sections), [sections]);

  const filteredSections = useMemo(() => {
    const term = normalizeText(query.trim());

    return sections.filter((section) => {
      const searchableText = [
        section.nom_section,
        section.discipline_section,
        section.responsable_section,
        section.mail_section,
        section.adresse_section,
      ]
        .filter(Boolean)
        .join(" ");

      const matchesSearch =
        !term || normalizeText(searchableText).includes(term);

      const matchesCategory =
        !selectedCategory ||
        String(section.category_id) === String(selectedCategory);

      const matchesStatus =
        !selectedStatus ||
        (selectedStatus === "active" && section.is_active !== false) ||
        (selectedStatus === "inactive" && section.is_active === false) ||
        (selectedStatus === "featured" && section.is_featured === true);

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [sections, query, selectedCategory, selectedStatus]);

  const visibleSections = useMemo(() => {
    return filteredSections.slice(0, visibleCount);
  }, [filteredSections, visibleCount]);

  const resetFilters = () => {
    setQuery("");
    setSelectedCategory("");
    setSelectedStatus("");
    setVisibleCount(CHUNK);
  };

  const loadMore = () => {
    setVisibleCount((current) =>
      Math.min(current + CHUNK, filteredSections.length),
    );
  };

  const openCreateDrawer = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    setDrawer({
      type: "create",
      section: null,
    });
  };

  const openEditDrawer = async (section) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      const fullSection = await getSectionById(section.id);

      setDrawer({
        type: "edit",
        section: fullSection || section,
      });
    } catch (error) {
      console.error("Erreur lors du chargement de l'association :", error);
      toast.error("Erreur lors du chargement de l'association.");

      setDrawer({
        type: "edit",
        section,
      });
    }
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      section: null,
    });
  };

  const handleCreateCategory = async (payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return null;
    }

    const label = String(payload?.label || "").trim();

    if (!label) {
      toast.error("Le nom de la catégorie est obligatoire.");
      return null;
    }

    try {
      setCategorySaving(true);

      const created = await createCategoryAsso({
        label,
        icon: payload?.icon || null,
        ordre:
          typeof payload?.ordre === "undefined" || payload?.ordre === ""
            ? 999
            : Number(payload.ordre),
        is_active: true,
      });

      if (!created?.id) {
        throw new Error("Catégorie non créée");
      }

      setCategories((current) =>
        sortCategories([
          ...current.filter((item) => String(item.id) !== String(created.id)),
          created,
        ]),
      );

      toast.success("Catégorie créée avec succès.");
      return created;
    } catch (error) {
      console.error("Erreur lors de la création de la catégorie :", error);
      toast.error("Erreur lors de la création de la catégorie.");
      return null;
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return false;
    }

    if (!category?.id) return false;

    const confirmed = window.confirm(
      `Supprimer la catégorie "${category.label}" ?`,
    );

    if (!confirmed) return false;

    try {
      const result = await deleteCategoryAsso(category.id);

      if (!result) {
        throw new Error("Suppression impossible");
      }

      setCategories((current) =>
        current.filter((item) => String(item.id) !== String(category.id)),
      );

      toast.success("Catégorie supprimée avec succès.");
      return true;
    } catch (error) {
      console.error("Erreur lors de la suppression de la catégorie :", error);
      toast.error("Erreur lors de la suppression de la catégorie.");
      return false;
    }
  };

  const handleCreateSection = async (payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await createSection(payload);

      toast.success("Association créée avec succès.");
      await fetchSections();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création de l'association :", error);
      toast.error("Erreur lors de la création de l'association.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSection = async (sectionId, payload) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    try {
      setSaving(true);

      await updateSection({
        id: sectionId,
        ...payload,
      });

      toast.success("Association mise à jour avec succès.");
      await fetchSections();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'association :", error);
      toast.error("Erreur lors de la mise à jour de l'association.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (section) => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    if (!section?.id) return;

    const confirmed = window.confirm(
      `Supprimer l'association "${section.nom_section}" ?`,
    );

    if (!confirmed) return;

    try {
      await deleteSection(section.id);

      setSections((current) =>
        current.filter((item) => String(item.id) !== String(section.id)),
      );

      toast.success("Association supprimée avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'association :", error);
      toast.error("Erreur lors de la suppression de l'association.");
    }
  };

  return {
    sections,
    categories,
    categoriesById,
    loading,
    saving,
    categorySaving,
    stats,
    query,
    selectedCategory,
    selectedStatus,
    visibleSections,
    filteredSections,
    visibleCount,
    drawer,
    demoMode,

    setQuery,
    setSelectedCategory,
    setSelectedStatus,
    resetFilters,
    loadMore,
    refreshData,

    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreateCategory,
    handleDeleteCategory,
    handleCreateSection,
    handleUpdateSection,
    handleDeleteSection,
  };
}
